// Commitment comments panel — Commitments-01c (Slack-style mentions).
//
// Lives inside the right rail (CommitmentDetailRightPanel) when the
// user picks the Comments tab. Renders a chronologically-sorted list
// above a composer with multi-line textarea and inline @-mention
// picker (Slack-style). Submit ships the human-readable body plus a
// structured ``mention_user_ids`` array; the backend uses the
// structured array for notification fan-out — no UUIDs leak into the
// rendered comment text.
//
// Mention UX:
// 1. Typing ``@`` opens the picker, anchored under the composer.
// 2. Subsequent keystrokes filter the picker by typed query.
// 3. Click / Enter inserts ``@Firstname Lastname `` into the body and
//    records the picked user_id in component state.
// 4. Escape closes the picker.
// 5. Backspace through the trailing space + label removes the
//    associated mention from the structured list.
//
// Private Commitments: mentions are disabled (the row isn't team-
// visible) and the composer surfaces "Private Commitments cannot
// mention other team members." next to the input.

import { Send, Sparkles, Trash2 } from "lucide-react";
import {
  useCallback, useContext, useEffect, useMemo, useRef, useState,
} from "react";

import { AuthContext } from "@/features/auth/AuthContext";
import { BeneAIMarkdown } from "@/features/beneai";

import {
  useAddCommitmentCommentMutation,
  useCommitmentCommentsQuery,
  useDeleteCommitmentCommentMutation,
  useMembersPickerQuery,
} from "./hooks";
import type { CommitmentComment } from "./types";


export interface CommitmentCommentsPanelProps {
  commitmentId: string;
  /** Commitments-01c — Private Commitments restrict mention reach.
   * Disable mention picker on private rows; the user can still write
   * a plain comment. */
  privateScope?: boolean;
}


interface PendingMention {
  /** Selected user id — submitted as ``mention_user_ids``. */
  userId: string;
  /** Human-readable label inserted into the body (``@Firstname Lastname``). */
  label: string;
}


/** Discriminated picker row.
 *
 *  ``member`` rows splice into ``pendingMentions`` and notify the
 *  matching User. ``agent`` rows insert the literal ``@BeneAI``
 *  token; the server's word-boundary parser handles the rest, and
 *  ``pendingMentions`` is left untouched because BeneAI is not a
 *  Membership row. */
type PickerEntry =
  | {
      kind: "member";
      member: { id: string; display_name: string; email: string; role?: string };
    }
  | { kind: "agent"; label: string; hint: string };


export function CommitmentCommentsPanel({
  commitmentId, privateScope = false,
}: CommitmentCommentsPanelProps) {
  const auth = useContext(AuthContext);
  const currentUserId = auth?.user?.id ?? null;
  const commentsQuery = useCommitmentCommentsQuery(commitmentId);
  const addMutation = useAddCommitmentCommentMutation(commitmentId);
  const deleteMutation = useDeleteCommitmentCommentMutation(commitmentId);
  const membersQuery = useMembersPickerQuery(!privateScope);

  const [draft, setDraft] = useState("");
  const [pendingMentions, setPendingMentions] = useState<PendingMention[]>([]);
  const [validationError, setValidationError] = useState<string | null>(null);
  const [pickerState, setPickerState] = useState<{
    open: boolean;
    /** Position of the leading ``@`` in ``draft`` so we know where
     * to splice the picked label back in. */
    triggerIndex: number;
    /** What the user has typed after the ``@`` so far. */
    query: string;
  }>({ open: false, triggerIndex: -1, query: "" });
  const [highlightedIndex, setHighlightedIndex] = useState(0);
  const textareaRef = useRef<HTMLTextAreaElement | null>(null);

  const rows = commentsQuery.data?.rows ?? [];
  const sortedRows = useMemo(() => {
    return rows.slice().sort((a, b) => {
      const aT = a.created_at ? new Date(a.created_at).getTime() : 0;
      const bT = b.created_at ? new Date(b.created_at).getTime() : 0;
      return aT - bT;
    });
  }, [rows]);

  const allMembers = useMemo(
    () => (membersQuery.data?.members ?? []).filter(
      (m) => m.id !== currentUserId,
    ),
    [membersQuery.data, currentUserId],
  );

  // ``@BeneAI`` detection drives the transient "thinking" indicator.
  // Same regex shape the backend uses in
  // ``agents_runtime.beneai_mention_service`` so the UI never lights
  // up the indicator for ``@beneai-foo`` strings the server would
  // reject. Case-insensitive; alpha-num-underscore-hyphen boundary
  // so handles like ``@beneai-helper`` never trigger.
  const draftMentionsBeneAI = useMemo(
    () => /(?<![A-Za-z0-9_-])@beneai(?![A-Za-z0-9_-])/i.test(draft),
    [draft],
  );

  const filteredMembers = useMemo(() => {
    const needle = pickerState.query.trim().toLowerCase();
    if (!needle) return allMembers;
    return allMembers.filter((m) => {
      const name = (m.display_name || "").toLowerCase();
      const email = (m.email || "").toLowerCase();
      return name.includes(needle) || email.includes(needle);
    });
  }, [allMembers, pickerState.query]);

  // BeneAI is the only non-Membership entity the picker exposes;
  // surface it at the top of the list whenever the typed query
  // matches (or the query is empty). Picking it inserts the literal
  // ``@BeneAI`` token + does NOT add anything to ``pendingMentions``
  // because BeneAI isn't a user — the server's word-boundary parser
  // is what triggers the runtime, not a structured id.
  const beneaiEntry: PickerEntry | null = useMemo(() => {
    const needle = pickerState.query.trim().toLowerCase();
    if (!needle) {
      return { kind: "agent", label: "BeneAI", hint: "Ask the assistant" };
    }
    if ("beneai".includes(needle) || "bene".includes(needle) || needle === "ai") {
      return { kind: "agent", label: "BeneAI", hint: "Ask the assistant" };
    }
    return null;
  }, [pickerState.query]);

  const pickerEntries = useMemo<PickerEntry[]>(() => {
    const entries: PickerEntry[] = [];
    if (beneaiEntry !== null) entries.push(beneaiEntry);
    for (const m of filteredMembers) {
      entries.push({ kind: "member", member: m });
    }
    return entries;
  }, [beneaiEntry, filteredMembers]);

  // Reset highlight when the filtered set changes.
  useEffect(() => {
    setHighlightedIndex(0);
  }, [pickerState.query, pickerState.open]);

  const closePicker = useCallback(() => {
    setPickerState({ open: false, triggerIndex: -1, query: "" });
  }, []);

  /** Recompute picker state from the current textarea contents +
   * caret position. Run on every change keystroke. */
  const recomputePickerFromCaret = useCallback(
    (value: string, caret: number) => {
      if (privateScope) {
        closePicker();
        return;
      }
      const upToCaret = value.slice(0, caret);
      const atIndex = upToCaret.lastIndexOf("@");
      if (atIndex === -1) {
        closePicker();
        return;
      }
      // Mention must follow whitespace or start of body so we don't
      // light up an email-looking string mid-word.
      const charBefore = atIndex === 0 ? "" : upToCaret[atIndex - 1];
      if (charBefore && !/\s/.test(charBefore)) {
        closePicker();
        return;
      }
      const between = upToCaret.slice(atIndex + 1);
      // Cancel the picker if the user has already moved past whitespace
      // (the @-token is finished — don't keep filtering).
      if (/\s/.test(between)) {
        closePicker();
        return;
      }
      setPickerState({
        open: true, triggerIndex: atIndex, query: between,
      });
    },
    [privateScope, closePicker],
  );

  const spliceLabelIntoDraft = useCallback((label: string) => {
    const { triggerIndex } = pickerState;
    if (triggerIndex < 0) return null;
    const before = draft.slice(0, triggerIndex);
    const after = draft.slice(
      textareaRef.current?.selectionStart ?? draft.length,
    );
    const replacement = `${label} `;
    const nextValue = `${before}${replacement}${after}`;
    setDraft(nextValue);
    closePicker();
    const nextCaret = before.length + replacement.length;
    requestAnimationFrame(() => {
      const ta = textareaRef.current;
      if (!ta) return;
      ta.focus();
      ta.setSelectionRange(nextCaret, nextCaret);
    });
    return { before, replacement };
  }, [draft, pickerState, closePicker]);

  const insertMention = useCallback(
    (member: { id: string; display_name: string; email: string }) => {
      const label = `@${member.display_name || member.email}`;
      const spliced = spliceLabelIntoDraft(label);
      if (spliced === null) return;
      setPendingMentions((prev) =>
        prev.some((m) => m.userId === member.id)
          ? prev
          : [...prev, { userId: member.id, label }],
      );
    },
    [spliceLabelIntoDraft],
  );

  const insertBeneAI = useCallback(() => {
    // Insert the literal ``@BeneAI`` token so the server's
    // word-boundary parser matches it the same way it matches a
    // hand-typed mention. No ``pendingMentions`` row — BeneAI isn't
    // a Membership.
    spliceLabelIntoDraft("@BeneAI");
  }, [spliceLabelIntoDraft]);

  const onPickEntry = useCallback((entry: PickerEntry) => {
    if (entry.kind === "member") {
      insertMention(entry.member);
    } else {
      insertBeneAI();
    }
  }, [insertMention, insertBeneAI]);

  const onTextareaChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    const next = e.target.value;
    setDraft(next);
    if (validationError) setValidationError(null);
    // Drop any pending mentions whose label no longer appears in the
    // body — Backspace through "@Alice Banks" should retract the
    // structured tag.
    setPendingMentions((prev) =>
      prev.filter((m) => next.includes(m.label)),
    );
    recomputePickerFromCaret(next, e.target.selectionStart ?? next.length);
  };

  const onTextareaKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (!pickerState.open) return;
    if (e.key === "Escape") {
      e.preventDefault();
      closePicker();
      return;
    }
    if (pickerEntries.length === 0) return;
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setHighlightedIndex((i) =>
        Math.min(i + 1, pickerEntries.length - 1),
      );
      return;
    }
    if (e.key === "ArrowUp") {
      e.preventDefault();
      setHighlightedIndex((i) => Math.max(i - 1, 0));
      return;
    }
    if (e.key === "Enter" || e.key === "Tab") {
      e.preventDefault();
      const entry = pickerEntries[highlightedIndex];
      if (entry) onPickEntry(entry);
    }
  };

  const onSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const trimmed = draft.trim();
    if (!trimmed) {
      setValidationError("Comment can't be empty.");
      return;
    }
    setValidationError(null);
    // Only ship mentions whose label is still present (Backspace handling).
    const liveMentions = pendingMentions.filter((m) =>
      trimmed.includes(m.label),
    );
    addMutation.mutate(
      {
        body: trimmed,
        ...(liveMentions.length > 0
          ? { mention_user_ids: liveMentions.map((m) => m.userId) }
          : {}),
      },
      {
        onSuccess: () => {
          setDraft("");
          setPendingMentions([]);
          closePicker();
        },
      },
    );
  };

  return (
    <div
      data-testid="commitment-detail-comments-tab"
      className="flex flex-1 min-h-0 flex-col"
    >
      <div
        data-testid="commitment-comments-list"
        className="flex-1 min-h-0 overflow-y-auto px-4 py-3"
      >
        {commentsQuery.isPending || commentsQuery.isLoading ? (
          <p
            data-testid="commitment-comments-loading"
            className="text-center text-xs text-text-tertiary"
          >
            Loading…
          </p>
        ) : sortedRows.length === 0 && !(
          addMutation.isPending && draftMentionsBeneAI
        ) ? (
          <p
            data-testid="commitment-comments-empty"
            className="text-center text-xs text-text-tertiary"
          >
            No comments yet
          </p>
        ) : (
          <ul role="list" className="flex flex-col gap-3">
            {sortedRows.map((c) => (
              <CommentItem
                key={c.comment_id}
                comment={c}
                isAuthor={
                  c.author_kind === "user"
                  && c.author_id === currentUserId
                }
                onDelete={() => deleteMutation.mutate(c.comment_id)}
                pendingDelete={deleteMutation.isPending}
              />
            ))}
            {/* Transient "BeneAI is thinking…" row while a comment
                containing ``@BeneAI`` is in flight. The backend
                responds with both the user comment and the agent
                reply in one shot, so this only shows during the
                synchronous turn. */}
            {addMutation.isPending && draftMentionsBeneAI && (
              <BeneAIThinkingItem />
            )}
          </ul>
        )}
      </div>
      <form
        onSubmit={onSubmit}
        data-testid="commitment-comments-composer"
        className="relative border-t border-border-subtle p-3"
      >
        <textarea
          ref={textareaRef}
          rows={2}
          data-testid="commitment-comments-input"
          value={draft}
          onChange={onTextareaChange}
          onKeyDown={onTextareaKeyDown}
          onBlur={() => {
            // Defer close so a click on a picker option still fires
            // before we tear it down.
            setTimeout(() => closePicker(), 120);
          }}
          placeholder={
            privateScope
              ? "Write a private comment…"
              : "Write a comment… (use @ to mention a teammate)"
          }
          disabled={addMutation.isPending}
          className="w-full resize-none rounded-md border border-border-subtle bg-canvas/40 px-3 py-2 text-xs text-text-primary outline-none focus:border-teal/50 focus:ring-1 focus:ring-teal/30 disabled:opacity-60"
        />
        {pickerState.open && !privateScope && (
          <MentionPicker
            entries={pickerEntries}
            highlightedIndex={highlightedIndex}
            onHover={setHighlightedIndex}
            onPick={onPickEntry}
          />
        )}
        {privateScope && (
          <p
            data-testid="commitment-comments-private-helper"
            className="mt-1 text-[11px] text-text-tertiary"
          >
            Private Commitments cannot mention other team members.
          </p>
        )}
        {validationError && (
          <p
            role="alert"
            data-testid="commitment-comments-validation-error"
            className="mt-1 text-[11px] font-medium text-ember-ink"
          >
            {validationError}
          </p>
        )}
        {addMutation.isError && (
          <p
            role="alert"
            data-testid="commitment-comments-error"
            className="mt-1 text-[11px] font-medium text-ember-ink"
          >
            {(addMutation.error as Error).message
              || "Couldn't post comment."}
          </p>
        )}
        <div className="mt-2 flex items-center justify-end gap-2">
          <button
            type="submit"
            data-testid="commitment-comments-submit"
            disabled={addMutation.isPending || draft.trim() === ""}
            className="inline-flex items-center gap-1 rounded-full bg-teal px-3 py-1 text-[11px] font-semibold text-text-inverse transition-fast ease-out hover:opacity-90 disabled:cursor-not-allowed disabled:opacity-60"
          >
            <Send size={12} aria-hidden="true" />
            Post
          </button>
        </div>
      </form>
    </div>
  );
}


function CommentItem({
  comment, isAuthor, onDelete, pendingDelete,
}: {
  comment: CommitmentComment;
  isAuthor: boolean;
  onDelete: () => void;
  pendingDelete: boolean;
}) {
  const kind = comment.author_kind ?? "user";
  const isAgent = kind === "agent";
  const isSystem = kind === "system";
  const author = isAgent
    ? "BeneAI"
    : isSystem
      ? "BeneAI · system"
      : (comment.author_display_name
        || comment.author_email
        || "Unknown user");
  return (
    <li
      data-testid={`commitment-comment-${comment.comment_id}`}
      data-author-kind={kind}
      className="flex items-start gap-2"
    >
      {isAgent || isSystem ? (
        <span
          aria-hidden="true"
          data-testid={`commitment-comment-beneai-avatar-${comment.comment_id}`}
          className="mt-0.5 inline-flex h-6 w-6 flex-shrink-0 items-center justify-center rounded-full bg-teal/10 text-teal"
        >
          <Sparkles size={12} />
        </span>
      ) : (
        <span
          aria-hidden="true"
          className="mt-0.5 inline-flex h-6 w-6 flex-shrink-0 items-center justify-center rounded-full bg-sage-tint text-[10px] font-semibold text-teal"
        >
          {computeInitials(author)}
        </span>
      )}
      <div className="flex min-w-0 flex-1 flex-col gap-0.5">
        <div className="flex items-center justify-between gap-2">
          <span className="flex items-center gap-1 text-xs font-medium text-text-primary">
            {author}
            {isAgent && (
              <span
                data-testid={`commitment-comment-beneai-badge-${comment.comment_id}`}
                className="rounded-full bg-teal/10 px-1.5 py-0.5 text-[9px] font-semibold uppercase tracking-wide text-teal"
              >
                Agent
              </span>
            )}
          </span>
          {comment.created_at && (
            <span className="text-[10px] text-text-tertiary">
              {formatTs(comment.created_at)}
            </span>
          )}
        </div>
        {isAgent || isSystem ? (
          // BeneAI returns markdown — render it. User mentions don't
          // appear in agent / system bodies (the assistant doesn't
          // populate ``mentioned_users``) so the chip-substitution
          // path isn't needed here.
          <BeneAIMarkdown
            body={comment.body}
            data-testid={`commitment-comment-body-${comment.comment_id}`}
            className="text-xs leading-relaxed text-text-secondary"
          />
        ) : (
          <p
            data-testid={`commitment-comment-body-${comment.comment_id}`}
            className="whitespace-pre-wrap text-xs leading-relaxed text-text-secondary"
          >
            {renderBodyWithMentions(
              comment.body,
              comment.mentioned_users ?? [],
            )}
          </p>
        )}
        {isAuthor && (
          <button
            type="button"
            data-testid={`commitment-comment-delete-${comment.comment_id}`}
            onClick={onDelete}
            disabled={pendingDelete}
            className="self-start text-[10px] text-text-tertiary transition-fast ease-out hover:text-ember-ink disabled:opacity-50"
          >
            <Trash2 size={10} className="inline" /> Delete
          </button>
        )}
      </div>
    </li>
  );
}


function BeneAIThinkingItem() {
  return (
    <li
      data-testid="commitment-comment-beneai-thinking"
      className="flex items-start gap-2"
    >
      <span
        aria-hidden="true"
        className="mt-0.5 inline-flex h-6 w-6 flex-shrink-0 items-center justify-center rounded-full bg-teal/10 text-teal"
      >
        <Sparkles size={12} />
      </span>
      <div className="flex min-w-0 flex-1 flex-col gap-0.5">
        <span className="text-xs font-medium text-text-primary">
          BeneAI
        </span>
        <span className="inline-flex items-center gap-1.5 text-xs text-text-tertiary">
          <span className="inline-flex gap-1" aria-hidden="true">
            <span
              className="h-1.5 w-1.5 animate-pulse rounded-full bg-teal"
              style={{ animationDelay: "0ms" }}
            />
            <span
              className="h-1.5 w-1.5 animate-pulse rounded-full bg-teal"
              style={{ animationDelay: "160ms" }}
            />
            <span
              className="h-1.5 w-1.5 animate-pulse rounded-full bg-teal"
              style={{ animationDelay: "320ms" }}
            />
          </span>
          BeneAI is thinking…
        </span>
      </div>
    </li>
  );
}


function MentionPicker({
  entries, highlightedIndex, onHover, onPick,
}: {
  entries: PickerEntry[];
  highlightedIndex: number;
  onHover: (i: number) => void;
  onPick: (entry: PickerEntry) => void;
}) {
  return (
    <div
      data-testid="commitment-comments-mention-picker"
      role="listbox"
      aria-label="Mention a teammate or BeneAI"
      className="absolute left-3 right-3 bottom-[calc(100%-0.5rem)] z-20 max-h-56 overflow-y-auto rounded-md border border-border-subtle bg-raised p-1 shadow-lg"
    >
      {entries.length === 0 ? (
        <p
          data-testid="commitment-comments-mention-empty"
          className="px-2 py-2 text-center text-[11px] text-text-tertiary"
        >
          No teammates match
        </p>
      ) : (
        <ul role="list" className="flex flex-col">
          {entries.map((entry, i) => (
            <li key={entry.kind === "member" ? entry.member.id : "beneai"}>
              <button
                type="button"
                role="option"
                aria-selected={i === highlightedIndex}
                data-testid={
                  entry.kind === "member"
                    ? `commitment-comments-mention-option-${entry.member.id}`
                    : "commitment-comments-mention-option-beneai"
                }
                onMouseDown={(e) => {
                  // Use mousedown so the click registers before the
                  // textarea's blur handler closes the picker.
                  e.preventDefault();
                  onPick(entry);
                }}
                onMouseEnter={() => onHover(i)}
                className={[
                  "flex w-full items-center justify-between gap-2 rounded px-2 py-1.5 text-left text-xs transition-fast ease-out",
                  i === highlightedIndex
                    ? "bg-recessed text-text-primary"
                    : "hover:bg-recessed text-text-primary",
                ].join(" ")}
              >
                {entry.kind === "agent" ? (
                  <>
                    <span className="flex min-w-0 items-center gap-1.5 font-medium">
                      <Sparkles
                        size={11} aria-hidden="true" className="text-teal"
                      />
                      <span className="truncate">{entry.label}</span>
                      <span className="rounded-full bg-teal/10 px-1.5 py-0.5 text-[9px] font-semibold uppercase tracking-wide text-teal">
                        Agent
                      </span>
                    </span>
                    <span className="truncate text-[10px] text-text-tertiary">
                      {entry.hint}
                    </span>
                  </>
                ) : (
                  <>
                    <span className="truncate font-medium">
                      {entry.member.display_name || entry.member.email}
                    </span>
                    {entry.member.email
                      && entry.member.email !== entry.member.display_name && (
                      <span className="truncate text-[10px] text-text-tertiary">
                        {entry.member.email}
                      </span>
                    )}
                  </>
                )}
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}


/** Replace ``@Display Name`` tokens (matching one of ``mentionedUsers``)
 * with chip-styled spans. Bare text is preserved verbatim. */
function renderBodyWithMentions(
  body: string,
  mentionedUsers: { user_id: string; display_name: string; email: string }[],
): React.ReactNode {
  if (!body) return body;
  if (mentionedUsers.length === 0) return body;
  // Build labels in descending length order so longer names ("@Alice
  // Banks-Smith") shadow shorter prefixes ("@Alice").
  const labels = mentionedUsers
    .map((u) => ({
      label: `@${u.display_name || u.email}`,
      user: u,
    }))
    .filter((entry) => entry.label.length > 1)
    .sort((a, b) => b.label.length - a.label.length);
  if (labels.length === 0) return body;

  const parts: React.ReactNode[] = [];
  let cursor = 0;
  let key = 0;
  while (cursor < body.length) {
    let matched: { label: string; user: { user_id: string; display_name: string; email: string } } | null = null;
    let matchIndex = -1;
    for (const entry of labels) {
      const idx = body.indexOf(entry.label, cursor);
      if (idx >= 0 && (matchIndex === -1 || idx < matchIndex)) {
        matchIndex = idx;
        matched = entry;
      }
    }
    if (!matched || matchIndex === -1) {
      parts.push(body.slice(cursor));
      break;
    }
    if (matchIndex > cursor) {
      parts.push(body.slice(cursor, matchIndex));
    }
    parts.push(
      <span
        key={`m-${key++}-${matchIndex}`}
        data-testid="commitment-comment-mention-chip"
        data-user-id={matched.user.user_id}
        className="rounded bg-teal/10 px-1 py-0.5 text-[11px] font-medium text-teal"
      >
        {matched.label}
      </span>,
    );
    cursor = matchIndex + matched.label.length;
  }
  return parts;
}


function computeInitials(name: string): string {
  const cleaned = name.trim();
  if (!cleaned) return "?";
  const parts = cleaned.split(/\s+/).filter(Boolean);
  if (parts.length === 1) {
    return cleaned.slice(0, 2).toUpperCase();
  }
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
}


function formatTs(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleString(undefined, {
    month: "short", day: "numeric",
    hour: "numeric", minute: "2-digit",
  });
}
